import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Users, X, FileText } from 'lucide-react';
import {
  createClass,
  getClasses,
  updateClass,
  deleteClass
} from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './Classes.css';

function Classes() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    gradeLevel: '',
    period: '',
    standards: ['']
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadClasses();
  }, [currentUser, navigate]);

  const loadClasses = async () => {
    setLoading(true);
    const result = await getClasses(currentUser.id);
    if (result.success) {
      setClasses(result.classes);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty standards
    const standards = formData.standards.filter(s => s.trim() !== '');

    const classData = {
      ...formData,
      standards
    };

    if (editingClass) {
      const result = await updateClass(editingClass.id, classData);
      if (result.success) {
        loadClasses();
        closeModal();
      }
    } else {
      const result = await createClass(currentUser.id, classData);
      if (result.success) {
        loadClasses();
        closeModal();
      }
    }
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setFormData({
      name: classItem.name,
      subject: classItem.subject || '',
      gradeLevel: classItem.gradeLevel || '',
      period: classItem.period || '',
      standards: classItem.standards && classItem.standards.length > 0 ? classItem.standards : ['']
    });
    setShowModal(true);
  };

  const handleDelete = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      const result = await deleteClass(classId);
      if (result.success) {
        loadClasses();
      }
    }
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClass(null);
    setFormData({
      name: '',
      subject: '',
      gradeLevel: '',
      period: '',
      standards: ['']
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading classes...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="classes-container">
        <div className="page-header">
          <div>
            <h1>Classes</h1>
            <p className="subtitle">Manage your classes and periods</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={20} />
            New Class
          </button>
        </div>

        {classes.length === 0 ? (
          <div className="empty-state">
            <Users size={64} className="empty-icon" />
            <h3>No classes yet</h3>
            <p>Create your first class to organize your lessons and units</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} />
              Create Class
            </button>
          </div>
        ) : (
          <div className="classes-grid">
            {classes.map(classItem => (
              <div key={classItem.id} className="class-card">
                <div className="card-header">
                  <Users className="class-icon" size={32} />
                  <div className="card-actions">
                    <button onClick={() => handleEdit(classItem)} className="btn-icon">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(classItem.id)} className="btn-icon delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <h3>{classItem.name}</h3>

                <div className="class-details">
                  <div className="detail-row">
                    <span className="detail-label">Subject:</span>
                    <span className="detail-value">{classItem.subject}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Grade Level:</span>
                    <span className="detail-value">{classItem.gradeLevel}</span>
                  </div>
                  {classItem.period && (
                    <div className="detail-row">
                      <span className="detail-label">Period:</span>
                      <span className="detail-value">{classItem.period}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate(`/planner/classes/${classItem.id}/scope`)}
                  className="btn-scope"
                >
                  <FileText size={18} />
                  Scope & Sequence
                </button>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingClass ? 'Edit Class' : 'Create New Class'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Class Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Algebra I - Period 3"
                  />
                </div>

                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    placeholder="e.g., Mathematics"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Grade Level *</label>
                    <input
                      type="text"
                      value={formData.gradeLevel}
                      onChange={(e) => setFormData({ ...formData, gradeLevel: e.target.value })}
                      required
                      placeholder="e.g., 9th Grade"
                    />
                  </div>

                  <div className="form-group">
                    <label>Period</label>
                    <input
                      type="text"
                      value={formData.period}
                      onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                      placeholder="e.g., 3rd Period"
                    />
                  </div>
                </div>

                {/* TEKS Standards */}
                <div className="form-group array-field">
                  <label>TEKS Standards</label>
                  {formData.standards.map((standard, index) => (
                    <div key={index} className="array-input-row">
                      <input
                        type="text"
                        value={standard}
                        onChange={(e) => handleArrayFieldChange('standards', index, e.target.value)}
                        placeholder="e.g., 111.39.c.1.A"
                      />
                      {formData.standards.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayField('standards', index)}
                          className="btn-icon-small delete"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('standards')}
                    className="add-field-btn"
                  >
                    + Add TEKS Standard
                  </button>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingClass ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Classes;
